<template>
  <modal
    title="Showcase Collection"
    size="full"
    :open="open"
    @close="closeModal"
  >
    <form @submit.stop.prevent="submit">
      <p>
        Showcasing your collection will allow others to see your collection on the Card Ladder Showcase tab. Your personal showcase will display your profile image and display name, and it will allow users to link to your profile to contact you about your cards.
      </p>

      <p>
        NOTE: None of your pricing information will be visible for others to see.
      </p>

      <image-upload
        v-model="account.photoUrl"
        name="collection-share-image"
        label="Profile Image"
        height="110"
        width="110"
      />

      <text-input
        id="showcase-displayName"
        v-model.trim="account.displayName"
        label="Display Name"
      />

      <text-input
        id="showcase-profileLink"
        v-model.trim="account.profileLink"
        type="url"
        label="Profile Link (Instagram, Facebook, Etc.)"
      />

      <text-input
        v-model.trim="collectionName"
        label="Collection Name"
        maxlength="16"
      />

      <div class="button-row">
        <btn
          type="submit"
          :loading="submitting"
          :disabled="disabled"
          loading-text="Submitting"
        >
          {{ isPublic ? 'Save Changes' : 'Save and Enable Showcase' }}
        </btn>

        <btn
          v-if="isPublic"
          kind="error"
          :loading="removing"
          loading-text="Removing"
          @click="removeFromShowcase"
        >
          Disable Showcase
        </btn>

        <icon-button v-if="isPublic" icon="link" @click="copyLink" />
      </div>
    </form>
  </modal>
</template>

<script>
import { mapState } from 'vuex';
import clone from 'lodash.clonedeep';
import * as Sentry from '@sentry/vue';

import { authService } from '@/services';

export default {
  data() {
    return {
      open: false,
      account: {},
      collectionName: '',
      submitting: false,
      removing: false,
    };
  },

  mounted() {
    this.$bus.$on('collection-share-setup', this.handle);
  },

  beforeDestroy() {
    this.$bus.$off('collection-share-setup', this.handle);
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      collection: (state) => state.collection,
    }),

    disabled() {
      return !this.account.displayName || !this.collectionName;
    },

    isPublic() {
      return this.collection?.public;
    },
  },

  methods: {
    handle() {
      this.account = {};
      this.collectionName = '';
      this.load();
      this.open = true;
    },

    load() {
      const user = clone(this.user);

      this.$set(this.account, 'displayName', user.displayName || '');
      this.$set(this.account, 'profileLink', user.profileLink || '');
      this.$set(this.account, 'photoUrl', user.photoUrl || '');

      this.collectionName = this.collection?.name || '';
    },

    async submit() {
      this.submitting = true;

      try {
        await authService.updateUser(this.user.uid, this.account);
        await authService.updateCollectionName(this.user.uid, this.collection.id, this.collectionName);
        await authService.setCollectionPublic(this.user.uid, this.collection.id, true);
        this.$toasted.success('Your collection is now showcased!');
        this.closeModal();
      } catch (e) {
        this.$toasted.error('Error showcasing your collection');
        Sentry.captureException(e);
      }

      this.submitting = false;
    },

    async removeFromShowcase() {
      if (window.confirm('Are you sure you want to remove your collection from the Showcase? You can also add it back later.')) {
        this.removing = true;

        try {
          await authService.setCollectionPublic(this.user.uid, this.collection.id, false);
          this.$toasted.success('The collection has been removed from Showcase!');
          this.closeModal();
        } catch (e) {
          this.$toasted.error('Failed to delete your collection');
          Sentry.captureException(e);
        }

        this.removing = false;
      }
    },

    copyLink() {
      this.$copyText(`${import.meta.env.VITE_DOMAIN}/showcase/${this.collection.uid}?collectionId=${this.collection.id}`);
      this.$toasted.success('Showcase link copied to your clipboard!');
    },

    closeModal() {
      this.open = false;
    },
  },
};
</script>

<style lang="scss" scoped>
form {
  margin: 0 auto;
  --image-upload-min-height: 200px;
}
</style>
